import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { AddNewsItemRecipientsResponse, NewsItemModel } from '../../../../api-service/clients';
import { Button } from '../../../../components/button';
import { SingleSelect } from '../../../../components/single-select';
import { isStillVisible } from '../news/news-item-visibility';
import { INudgeCitizen } from './nudge-citizen-list';
import { useNudgeNotifier } from './use-nudge-notifier';

const client = moduleClientFactory.createForumCaseworkerClient();

export interface INudgeWithNewsProps {
  citizens: INudgeCitizen[];
}

export function NudgeWithNews(props: INudgeWithNewsProps) {
  const { citizens } = props;
  const [newsItems, setNewsItems] = useState<NewsItemModel[]>([]);
  const [newsItemId, setNewsItemId] = useState('');
  const [isSending, setIsSending] = useState(false);
  const { notify, notifyLoadError } = useNudgeNotifier();

  useEffect(() => {
    client
      .getNewsItems()
      .then(result => setNewsItems((result.newsItems ?? []).filter(newsItem => isStillVisible(newsItem) && !newsItem.showToAll)))
      .catch(error => {
        setNewsItems([]);
        notifyLoadError(error, 'Nyhederne kunne ikke hentes.');
      });
  }, []);

  async function send() {
    setIsSending(true);
    try {
      const outcome = await client.addNewsItemRecipients({ newsItemId, citizenIds: citizens.map(citizen => citizen.citizenId) });
      notify(newsOutcomeText(outcome), outcome.failedCount > 0 ? 'info' : 'success');
    } catch {
      notify('Nyheden blev ikke sendt.', 'error');
    } finally {
      setIsSending(false);
    }
  }

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <SingleSelect<NewsItemModel>
        label="Nyhed"
        value={newsItems.find(newsItem => newsItem.id === newsItemId)}
        options={newsItems}
        getOptionLabel={newsItem => newsItem.title ?? ''}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderOption={(optionProps, newsItem) => (
          <li {...optionProps} key={newsItem.id}>
            {newsItem.title}
          </li>
        )}
        onChange={newsItem => setNewsItemId(newsItem.id)}
        disableClearable
      />
      <Box display="flex" justifyContent="flex-end">
        <Button color="primary" onClick={send} disabled={!newsItemId || citizens.length === 0} loading={isSending}>
          Send nyhed
        </Button>
      </Box>
    </Box>
  );
}

export function newsOutcomeText(outcome: AddNewsItemRecipientsResponse) {
  const parts = [`Sendt til ${outcome.addedCount} ${outcome.addedCount === 1 ? 'borger' : 'borgere'}.`];
  if (outcome.alreadyRecipientCount > 0) {
    parts.push(`${outcome.alreadyRecipientCount} havde den i forvejen.`);
  }
  if (outcome.failedCount > 0) {
    parts.push(`${outcome.failedCount} kunne ikke findes.`);
  }
  return parts.join(' ');
}
